/**
 * Usage (S-16, spec §6) — tokens and time, by model and role.
 *
 * What the harnesses themselves report, summed from the attempts: exact where
 * a run said so, unknown where nothing did — never a zero pretending to be a
 * measurement. Money is deliberately absent from the table: these agents run
 * on subscription CLIs that price nothing per call, and a fabricated rate
 * would be a number somebody eventually trusts.
 */

import { useState, type ReactNode } from 'react';

import { Button } from '@primer/react';
import { DownloadIcon } from '@primer/octicons-react';

import { useUsage } from '../queries.js';
import { QueryPhase, Section } from '../components/state.js';
import { useStore } from '../store-context.js';

function formatTokens(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(value);
}

function formatDuration(ms: number): string {
  if (ms >= 3_600_000) return `${(ms / 3_600_000).toFixed(1)}h`;
  if (ms >= 60_000) return `${(ms / 60_000).toFixed(1)}m`;
  return `${Math.round(ms / 1000)}s`;
}

export function UsageScreen(): ReactNode {
  const store = useStore();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const usage = useUsage({ ...(from.length > 0 ? { from } : {}), ...(to.length > 0 ? { to } : {}) });

  const download = (format: 'csv' | 'json'): void => {
    void store.data
      .usageExport(format, { ...(from.length > 0 ? { from } : {}), ...(to.length > 0 ? { to } : {}) })
      .then((text) => {
        const blob = new Blob([text], { type: format === 'csv' ? 'text/csv' : 'application/json' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `usage.${format}`;
        anchor.click();
        URL.revokeObjectURL(url);
      });
  };

  const rows = usage.data ?? [];
  const totals = rows.reduce(
    (sum, row) => ({
      input: sum.input + row.inputTokens,
      output: sum.output + row.outputTokens,
      attempts: sum.attempts + row.attempts,
    }),
    { input: 0, output: 0, attempts: 0 },
  );

  return (
    <div className="stack">
      <Section
        label="Tokens by model and role"
        lede="Summed from the attempts, as each harness reported its own runs. Unknown stays unknown — no invented zeroes."
      >
        <div className="usage-toolbar">
          <label className="field-label" htmlFor="usage-from">
            From
          </label>
          <input id="usage-from" type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          <label className="field-label" htmlFor="usage-to">
            To
          </label>
          <input id="usage-to" type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          <Button leadingVisual={DownloadIcon} onClick={() => download('csv')}>
            CSV
          </Button>
          <Button leadingVisual={DownloadIcon} onClick={() => download('json')}>
            JSON
          </Button>
        </div>

        {usage.isPending || usage.error !== null || rows.length === 0 ? (
          <QueryPhase
            isPending={usage.isPending}
            error={usage.error}
            isEmpty={rows.length === 0}
            source="usage"
            emptyTitle="Nothing measured yet"
            emptyDetail="Token counts appear as agents finish runs."
            onRetry={() => void usage.refetch()}
            rows={3}
          />
        ) : (
          <div className="usage-table-wrap">
            <table className="usage-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Model</th>
                  <th>Attempts</th>
                  <th>Input</th>
                  <th>Output</th>
                  <th>Calls</th>
                  <th>Time</th>
                  <th>Measured</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.role}|${row.model}`}>
                    <td>{row.role}</td>
                    <td>{row.model}</td>
                    <td>{row.attempts}</td>
                    <td>{formatTokens(row.inputTokens)}</td>
                    <td>{formatTokens(row.outputTokens)}</td>
                    <td>{row.providerCalls}</td>
                    <td>{formatDuration(row.computeMs)}</td>
                    <td>
                      {row.exact}/{row.attempts}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>Total</td>
                  <td>{totals.attempts}</td>
                  <td>{formatTokens(totals.input)}</td>
                  <td>{formatTokens(totals.output)}</td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
